<template>
  <div class="emprega-curitiba-oportunidades">
    <!-- Hero Section -->
    <section class="eco-hero">
      <div class="eco-hero__bg">
        <img
          :src="imageUrls.heroBackground"
          alt="Curitiba Background"
          class="eco-hero__bg-img"
          referrerpolicy="no-referrer"
        />
        <div class="eco-hero__overlay" />
      </div>
      <div class="eco-hero__content">
        <div class="eco-hero__card">
          <h1 class="eco-hero__title">{{ pageContent.title }}</h1>
          <div class="eco-hero__intro">
            <p
              v-for="(paragraph, index) in pageContent.intro"
              :key="index"
              class="eco-hero__paragraph"
            >
              {{ paragraph }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <main class="eco-main">
      <!-- Job Seeker Section -->
      <section class="eco-section eco-section--job-seeker">
        <div class="eco-section__header">
          <div class="eco-section__icon eco-section__icon--light">
            <BriefcaseIcon class="eco-section__icon-svg" />
          </div>
          <h2 class="eco-section__title">{{ pageContent.jobSeekerTitle }}</h2>
        </div>
        <div class="eco-grid">
          <article
            v-for="(item, index) in jobSeekerItems"
            :key="item.id"
            class="eco-card eco-card--stagger"
            :style="{ animationDelay: `${index * 100}ms` }"
          >
            <div class="eco-card__image-wrap">
              <img
                :src="getCardImageUrl(item)"
                :alt="item.title"
                class="eco-card__image"
                referrerpolicy="no-referrer"
              />
              <div class="eco-card__image-overlay" />
            </div>
            <div class="eco-card__body">
              <h3 class="eco-card__title">{{ item.title }}</h3>
              <p class="eco-card__description">{{ item.description }}</p>
              <a
                :href="item.linkUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="eco-card__link"
              >
                {{ item.linkText }}
                <ChevronRightIcon class="eco-card__link-icon" />
              </a>
            </div>
          </article>
        </div>
      </section>

      <!-- Entrepreneur Section -->
      <section class="eco-section eco-section--entrepreneur eco-section--last">
        <div class="eco-section__deco" />
        <div class="eco-section__inner">
          <div class="eco-section__header-block">
            <div class="eco-section__header">
              <div class="eco-section__icon eco-section__icon--dark">
                <SparklesIcon class="eco-section__icon-svg" />
              </div>
              <h2 class="eco-section__title eco-section__title--white">
                {{ pageContent.entrepreneurTitle }}
              </h2>
            </div>
            <p class="eco-section__intro">{{ pageContent.entrepreneurIntro }}</p>
          </div>
          <div class="eco-grid">
            <article
              v-for="(item, index) in entrepreneurItems"
              :key="item.id"
              class="eco-card eco-card--dark eco-card--stagger"
              :style="{ animationDelay: `${index * 100}ms` }"
            >
              <div class="eco-card__image-wrap">
                <img
                  :src="getCardImageUrl(item)"
                  :alt="item.title"
                  class="eco-card__image"
                  referrerpolicy="no-referrer"
                />
                <div class="eco-card__image-overlay" />
              </div>
              <div class="eco-card__body">
                <h3 class="eco-card__title eco-card__title--white">{{ item.title }}</h3>
                <p class="eco-card__description eco-card__description--light">
                  {{ item.description }}
                </p>
                <a
                  :href="item.linkUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="eco-card__link eco-card__link--light"
                >
                  {{ item.linkText }}
                  <ChevronRightIcon class="eco-card__link-icon" />
                </a>
              </div>
            </article>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import companyMixin from "@state/company";
import { BriefcaseIcon, ChevronRightIcon, SparklesIcon } from "@vue-hero-icons/outline";

const PAGE_CONTENT = {
  title: "Curitiba conecta você ao emprego",
  intro: [
    "A Prefeitura de Curitiba, por meio da Secretaria Municipal de Desenvolvimento Econômico e Inovação, trabalha para ser a ponte entre os curitibanos e uma nova oportunidade de emprego. Esta plataforma de empregos foi criada para diminuir as distâncias entre quem procura emprego e as empresas que têm uma vaga para oferecer.",
    "Acreditamos que uma cidade próspera é aquela que investe nos moradores. Por isso, modernizamos o ambiente de negócios e criamos redes de apoio que facilitam desde a qualificação profissional até a recolocação no mercado, sempre com foco na melhoria da qualidade de vida da capital.",
    "Além desta plataforma, a Prefeitura oferece diversos programas presenciais e auxílios para ajudar você a conquistar sua vaga.",
  ],
  jobSeekerTitle: "Para quem procura emprego",
  entrepreneurTitle: "Apoio para quem quer empreender",
  entrepreneurIntro:
    "Se você tem o sonho de abrir o seu próprio negócio ou quer fazer sua pequena empresa crescer, Curitiba oferece o suporte necessário:",
};

const OPPORTUNITIES_DATA = [
  {
    id: "sine-municipal",
    title: "Sine Municipal",
    description:
      "Sistema Público de Empregos que conecta de forma gratuita trabalhadores e empresas, oferecendo consulta de vagas de emprego e de cursos profissionalizantes, encaminhamento para entrevistas e para os Centros de Formação Profissional parceiros e suporte ao seguro-desemprego.",
    linkText: "Veja endereços aqui",
    linkUrl: "https://servicos.curitiba.pr.gov.br/sine-municipal-vagas-de-emprego/795",
    imageUrl: "https://assets.selecty.app/customize/pmc/pmc/oportunidade2.jpeg",
    category: "job-seeker",
  },
  {
    id: "sine-movel",
    title: "Sine Móvel",
    description:
      "Unidades itinerantes do Sine Municipal que levam a oferta dos serviços para mais perto da sua casa.",
    linkText: "Veja agenda aqui",
    linkUrl: "https://servicos.curitiba.pr.gov.br/sine-movel/967",
    imageUrl: "https://assets.selecty.app/customize/pmc/pmc/oportunidade3.jpeg",
    category: "job-seeker",
  },
  {
    id: "tarifa-zero",
    title: "Tarifa Zero a Caminho do Emprego",
    description:
      "Passagem de ônibus gratuita para que quem está desempregado, é morador de Curitiba e tem um encaminhamento para entrevista emitido pelo Sine Municipal possa ir até as entrevistas de emprego sem custos.",
    linkText: "Saiba mais",
    linkUrl: "https://servicos.curitiba.pr.gov.br/tarifa-zero-a-caminho-do-emprego/991",
    imageUrl: "https://assets.selecty.app/customize/pmc/pmc/oportunidade4.jpeg",
    category: "job-seeker",
  },
  {
    id: "liceu-oficios",
    title: "Liceu de Ofícios",
    description:
      "Centros de capacitação com cursos gratuitos para você aprender uma nova profissão ou melhorar seu currículo.",
    linkText: "Saiba mais",
    linkUrl: "https://servicos.curitiba.pr.gov.br/liceu-de-oficios-cursos/756",
    imageUrl: "https://assets.selecty.app/customize/pmc/pmc/oportunidade5.jpeg",
    category: "job-seeker",
  },
  {
    id: "vale-qualificacao",
    title: "Vale-Qualificação",
    description:
      "A iniciativa permite que trabalhadores residentes em Curitiba, que estejam desempregados, escolham um curso de qualificação profissional de acordo com a área com a qual mais se identificam.",
    linkText: "Saiba mais",
    linkUrl:
      "https://www.curitiba.pr.gov.br/noticias/abertas-as-inscricoes-para-os-cursos-do-vale-qualificacao-da-prefeitura-de-curitiba/81375",
    imageUrl: "https://assets.selecty.app/customize/pmc/pmc/oportunidade6.jpeg",
    category: "job-seeker",
  },
  {
    id: "microcredito",
    title: "Microcrédito mais barato",
    description:
      "Parceria entre a Prefeitura de Curitiba e o Governo do Paraná para empréstimos com taxas de juros reduzidas para MEIs e pequenos empreendedores investirem em seus negócios.",
    linkText: "Solicite agora",
    linkUrl: "https://www.fomento.pr.gov.br/Microcredito-Facil",
    imageUrl: "https://assets.selecty.app/customize/pmc/pmc/oportunidade7.jpeg",
    category: "entrepreneur",
  },
  {
    id: "facilita-mais",
    title: "Facilita Mais",
    description:
      "Menos burocracia e muito mais agilidade para abrir sua empresa e liberar alvarás de forma automática.",
    linkText: "Saiba mais",
    linkUrl: "https://servicos.curitiba.pr.gov.br/facilita-mais/1012",
    imageUrl: "https://assets.selecty.app/customize/pmc/pmc/oportunidade8.jpeg",
    category: "entrepreneur",
  },
  {
    id: "empreendedora-curitibana",
    title: "Programa Empreendedora Curitibana",
    description:
      "Programa que incentiva o protagonismo feminino nos negócios através de capacitação, redes de contatos e reconhecimento com premiações para mulheres que empreendem em Curitiba.",
    linkText: "Seja uma empreendedora",
    linkUrl:
      "https://agenciacuritiba.com.br/programas/curitiba-empreendedora/espaco-empreendedor/",
    imageUrl: "https://assets.selecty.app/customize/pmc/pmc/oportunidade9.png",
    category: "entrepreneur",
  },
  {
    id: "sala-empreendedor",
    title: "Sala do Empreendedor",
    description:
      "Unidades de apoio nas Ruas da Cidadania que oferecem consultoria gratuita, formalização de MEI e orientações estratégicas para quem já tem ou deseja abrir o próprio negócio.",
    linkText: "Saiba mais",
    linkUrl:
      "https://servicos.curitiba.pr.gov.br/mei-atendimento-no-espaco-empreendedor/366",
    imageUrl: "https://assets.selecty.app/customize/pmc/pmc/oportunidade10.jpeg",
    category: "entrepreneur",
  },
];

export default {
  mixins: [companyMixin],
  components: {
    BriefcaseIcon,
    ChevronRightIcon,
    SparklesIcon,
  },
  data() {
    return {
      pageContent: PAGE_CONTENT,
      opportunitiesData: OPPORTUNITIES_DATA,
      imageUrls: {
        heroBackground: "https://assets.selecty.app/customize/pmc/pmc/oportunidade1.png",
      },
    };
  },
  computed: {
    jobSeekerItems() {
      return this.opportunitiesData.filter((item) => item.category === "job-seeker");
    },
    entrepreneurItems() {
      return this.opportunitiesData.filter((item) => item.category === "entrepreneur");
    },
  },
  created() {
    if (!this.company?.config?.pages?.emprega_curitiba_oportunidades) {
      this.$router.push({ name: "home" });
    }
  },
  methods: {
    getCardImageUrl(item) {
      return this.imageUrls[item.id] || item.imageUrl;
    },
  },
};
</script>

<style lang="scss" scoped>
$curitiba-blue: #2563eb;
$curitiba-blue-hover: #1d4ed8;
$curitiba-dark-blue: #174284;
$bg-page: #f3f4f6;
$slate-800: #1e293b;
$slate-600: #475569;
$white-90: rgba(255, 255, 255, 0.9);

.emprega-curitiba-oportunidades {
  background: $bg-page;
  color: $slate-800;
  min-height: 100%;
}

@keyframes eco-fade-in-up {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.eco-hero {
  position: relative;
  display: flex;
  align-items: center;
  overflow: hidden;
  padding: 60px 0;
}

.eco-hero__bg {
  position: absolute;
  inset: 0;
  z-index: 0;
}

.eco-hero__bg-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scale(1.05);
  filter: blur(2px);
}

.eco-hero__overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
}

.eco-hero__content {
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 24px;
  width: 100%;
  position: relative;
  z-index: 10;
}

@media (min-width: 768px) {
  .eco-hero__content {
    padding: 0 48px;
  }
}

.eco-hero__card {
  max-width: 48rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 12px;
  border-radius: 40px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  color: #fff;
  animation: eco-fade-in-up 0.6s ease-out forwards;
}

@media (min-width: 768px) {
  .eco-hero__card {
    padding: 44px;
  }
}

.eco-hero__title {
  font-size: 2.25rem;
  font-weight: 900;
  margin-bottom: 2rem;
  letter-spacing: -0.025em;
  line-height: 1.2;
}

@media (min-width: 768px) {
  .eco-hero__title {
    font-size: 3.75rem;
  }
}

.eco-hero__intro {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.eco-hero__paragraph {
  font-size: 1.125rem;
  line-height: 1.75;
  font-weight: 500;
  color: $white-90;
}

@media (min-width: 768px) {
  .eco-hero__paragraph {
    font-size: 1.25rem;
  }
}

.eco-main {
  max-width: 1440px;
  margin: 0 auto;
  padding: 60px 24px 0;
}

@media (min-width: 768px) {
  .eco-main {
    padding-left: 48px;
    padding-right: 48px;
  }
}

.eco-section {
  margin-bottom: 80px;
}

.eco-section--last {
  margin-bottom: 0;
}

.eco-section__header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 3rem;
}

.eco-section__icon {
  width: 48px;
  height: 48px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.eco-section__icon--light {
  background: #eff6ff;
  color: $curitiba-blue;
}

.eco-section__icon--dark {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  backdrop-filter: blur(4px);
}

.eco-section__icon-svg {
  width: 28px;
  height: 28px;
}

.eco-section__title {
  font-size: 1.875rem;
  font-weight: 700;
  color: $slate-800;
  margin: 0;
}

.eco-section__title--white {
  color: #fff;
}

.eco-section__header-block {
  max-width: 56rem;
  margin-bottom: 3rem;
}

.eco-section__header-block .eco-section__header {
  margin-bottom: 1.5rem;
}

.eco-section__intro {
  font-size: 1.25rem;
  font-weight: 500;
  color: rgba(191, 219, 254, 0.9);
  margin: 0;
}

.eco-section--entrepreneur {
  background: $curitiba-dark-blue;
  border-radius: 40px;
  padding: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  position: relative;
}

@media (min-width: 768px) {
  .eco-section--entrepreneur {
    padding: 44px;
  }
}

.eco-section__deco {
  position: absolute;
  top: 0;
  right: 0;
  width: 33.333%;
  height: 100%;
  background: rgba(255, 255, 255, 0.05);
  transform: skewX(-12deg) translateX(50%);
}

.eco-section__inner {
  position: relative;
  z-index: 10;
}

.eco-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2.5rem;
}

@media (min-width: 768px) {
  .eco-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1024px) {
  .eco-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 2.5rem;
  }
}

.eco-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  height: 100%;
  transition: box-shadow 0.3s ease, transform 0.3s ease;
}

.eco-card:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  transform: translateY(-5px);
}

.eco-card--dark {
  background: #fff;
}

.eco-card--stagger {
  opacity: 0;
  animation: eco-fade-in-up 0.6s ease-out forwards;
}

.eco-card__image-wrap {
  position: relative;
  height: 192px;
  overflow: hidden;
}

.eco-card:hover .eco-card__image {
  transform: scale(1.05);
}

.eco-card__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.eco-card__image-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.2), transparent);
}

.eco-card__body {
  padding: 24px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.eco-card__title {
  font-size: 1.25rem;
  font-weight: 600;
  color: $slate-800;
  margin: 0 0 12px;
  transition: color 0.2s;
}

.eco-card:hover .eco-card__title {
  color: $curitiba-blue;
}

.eco-card__title--white {
  color: $slate-800;
}

.eco-card__description {
  font-size: 0.875rem;
  line-height: 1.625;
  color: $slate-600;
  margin: 0 0 24px;
  flex-grow: 1;
}

.eco-card__description--light {
  color: $slate-600;
}

.eco-card__link {
  display: inline-flex;
  align-items: center;
  color: $curitiba-blue;
  font-weight: 700;
  font-size: 0.875rem;
  text-decoration: none;
  transition: color 0.2s;
}

.eco-card__link:hover {
  text-decoration: underline;
  color: $curitiba-blue-hover;
}

.eco-card__link--light {
  color: $curitiba-blue;
}

.eco-card__link-icon {
  width: 16px;
  height: 16px;
  margin-left: 8px;
  transition: transform 0.2s;
}

.eco-card__link:hover .eco-card__link-icon {
  transform: translateX(4px);
}
</style>
